import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  payload: Record<string, unknown> | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
  /** Workspace the notification belongs to (null = account-level / global). */
  workspacePublicId: string | null;
  workspaceName: string | null;
}

export function useNotifications(opts?: { unread?: boolean }) {
  return useQuery({
    queryKey: ['notifications', opts?.unread ? 'unread' : 'all'],
    queryFn: () => {
      const params = new URLSearchParams();
      if (opts?.unread) params.set('unread', 'true');
      params.set('limit', '20');
      const qs = params.toString();
      return api<{ data: Notification[] }>(`/notifications?${qs}`);
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: () => api<{ unread: number }>('/notifications/count'),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
